import { EventMsgError } from './event-msg-error.js';

/**
 * Error for operations that exceed their timeout duration
 */
export class TimeoutError extends EventMsgError {
  /** Timeout duration in milliseconds */
  readonly timeoutMs: number;

  /** Operation that timed out */
  readonly operation: string;

  constructor(
    message: string,
    timeoutMs: number,
    operation = 'operation',
    options: {
      context?: Record<string, unknown>;
      solutions?: string[];
      cause?: Error;
    } = {}
  ) {
    super(message, 'TIMEOUT_ERROR', {
      ...options,
      context: {
        ...options.context,
        timeoutMs,
        operation,
      },
      solutions: [
        'Increase timeout duration',
        'Check network connectivity',
        'Verify target device is responding',
        'Reduce message frequency',
      ],
    });

    this.timeoutMs = timeoutMs;
    this.operation = operation;
  }
}

/**
 * Send operation timed out
 */
export class SendTimeoutError extends TimeoutError {
  constructor(
    timeoutMs: number,
    options: {
      context?: Record<string, unknown>;
      cause?: Error;
    } = {}
  ) {
    super(`Send operation timed out after ${timeoutMs}ms`, timeoutMs, 'send', {
      ...options,
      solutions: [
        'Increase send timeout',
        'Check transport connection',
        'Verify target device is reachable',
        'Check for network congestion',
      ],
    });

    this.code = 'SEND_TIMEOUT_ERROR';
  }
}

/**
 * WaitFor operation timed out
 */
export class WaitForTimeoutError extends TimeoutError {
  /** Event name that was being waited for */
  readonly eventName: string;

  constructor(
    eventName: string,
    timeoutMs: number,
    options: {
      context?: Record<string, unknown>;
      cause?: Error;
    } = {}
  ) {
    super(
      `waitFor('${eventName}') timed out after ${timeoutMs}ms`,
      timeoutMs,
      'waitFor',
      {
        ...options,
        context: {
          ...options.context,
          eventName,
        },
        solutions: [
          'Increase waitFor timeout',
          'Check if event name is correct',
          'Verify sender is responding',
          'Check event filter conditions',
        ],
      }
    );

    this.eventName = eventName;
    this.code = 'WAIT_FOR_TIMEOUT_ERROR';
  }
}

/**
 * Connection timeout error
 */
export class ConnectionTimeoutError extends TimeoutError {
  constructor(
    timeoutMs: number,
    options: {
      context?: Record<string, unknown>;
      cause?: Error;
    } = {}
  ) {
    super(
      `Connection attempt timed out after ${timeoutMs}ms`,
      timeoutMs,
      'connect',
      {
        ...options,
        solutions: [
          'Increase connection timeout',
          'Check device availability',
          'Verify connection parameters',
          'Check for interference',
        ],
      }
    );

    this.code = 'CONNECTION_TIMEOUT_ERROR';
  }
}
